import { useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

export function InviteUser({ onSent }: { onSent: (message: string) => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ email: string; sandbox: boolean }>('/auth/invitations', {
        method: 'POST',
        json: { email: email.trim().toLowerCase() },
      });
      onSent(
        result.sandbox
          ? `Sandbox invitation sent to the configured test inbox, not ${result.email}.`
          : `Invitation sent to ${result.email}. They'll appear here as an Editor after signing in with Google.`,
      );
      dialog.current?.close();
    } catch (err) {
      setError(errMsg(err, 'Could not send invitation'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        className="cursor-pointer"
        onClick={() => {
          setEmail('');
          setError(null);
          dialog.current?.showModal();
        }}
      >
        Invite user
      </Button>
      <dialog
        ref={dialog}
        aria-labelledby="invite-title"
        aria-describedby="invite-description"
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-xl border bg-background p-6 text-foreground shadow-lg backdrop:bg-black/40"
        onCancel={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <form onSubmit={submit} className="flex flex-col gap-4" aria-busy={busy}>
          <h2 id="invite-title" className="text-xl">
            Invite user
          </h2>
          <p id="invite-description" className="text-sm text-muted-foreground">
            Send an invitation to join as an Editor. They'll need to sign in with the same Google
            email address within 7 days.
          </p>
          <label className="flex flex-col gap-1 text-sm">
            Email
            <input
              autoFocus
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              disabled={busy}
              onChange={(event) => setEmail(event.target.value)}
              className="h-10 w-full rounded-md border px-3"
              placeholder="name@example.com"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              disabled={busy}
              onClick={() => dialog.current?.close()}
            >
              Cancel
            </Button>
            <Button type="submit" className="cursor-pointer" disabled={busy || !email.trim()}>
              {busy ? 'Sending…' : 'Invite'}
            </Button>
          </div>
        </form>
      </dialog>
    </>
  );
}
